/**
 * The UI languages of the Ibada Cloud apps, and the language every API response is localized in (ADR-0030).
 * Kinyarwanda is resolvable while it is paused in the UI, so a stored `rw` preference still resolves.
 */
export enum AppLanguage {
  French = "fr",
  English = "en",
  Kinyarwanda = "rw"
}

/** The platform default when nothing else names a language. */
export const DEFAULT_APP_LANGUAGE = AppLanguage.French;

/**
 * The header the apps send on every API request with the current UI language. A custom header rather than
 * `Accept-Language`: browsers send the latter on their own, which would silently override the user's stored
 * preference with the operating system's language.
 */
export const LOCALE_HEADER = "x-locale";

const APP_LANGUAGES = new Set<string>(Object.values(AppLanguage));

/**
 * A raw tag ("fr", "fr-FR", "EN", "en_US") as an {@link AppLanguage}, or null when it names none of ours.
 * Only the primary subtag matters: an English-Rwanda browser is English.
 */
export const parseAppLanguage = (raw: string | null | undefined): AppLanguage | null => {
  if (!raw) return null;
  const primary = raw.trim().toLowerCase().split(/[-_]/)[0] ?? "";
  return APP_LANGUAGES.has(primary) ? (primary as AppLanguage) : null;
};

/** The first of our languages named by an `Accept-Language` header, in the header's order. */
export const parseAcceptLanguage = (header: string | null | undefined): AppLanguage | null => {
  if (!header) return null;
  for (const part of header.split(",")) {
    const language = parseAppLanguage(part.split(";")[0]);
    if (language) return language;
  }
  return null;
};
